import { useState, useRef, useEffect } from 'react';
import API_BASE_URL from '../config';
import toast from 'react-hot-toast';
import { aufTaste } from '../utils/tastaturBedienung';
import { standJeSeite } from '../utils/freigabeStand';

const CONTENT_TABS = [
  { id: 'inhalte',    label: 'Seiteninhalte',   icon: '📄', desc: 'Texte & KI' },
  { id: 'assets',     label: 'Bilder & Assets', icon: '🖼️', desc: 'Medien je Seite' },
  { id: 'freigaben',  label: 'Freigaben',       icon: '✅', desc: 'Kunden-Freigaben' },
];

export default function ContentWerkstatt({ project, sitemapPages, sitemapLoading, token, leadId, websiteContent, onProjectRefresh, mode }) {
  const [activeTab, setActiveTab]       = useState(mode || 'inhalte');
  const [selectedPage, setSelectedPage] = useState(null);
  const [generating, setGenerating]     = useState(false);
  const [pageContent, setPageContent]   = useState({});
  const [editedContent, setEditedContent] = useState({});
  const [newContent, setNewContent]     = useState({});
  const [queueRunning, setQueueRunning]         = useState(false);
  const [queueDone, setQueueDone]               = useState(0);
  const [queueStop, setQueueStop]               = useState(false);
  const [saveStatus, setSaveStatus]             = useState('idle');
  const [queueCurrentPage, setQueueCurrentPage] = useState('');
  const [generatingAll, setGeneratingAll]       = useState(false);
  const [allGenProgress, setAllGenProgress]     = useState({ done: 0, total: 0 });
  const queueStopRef = useRef(false);

  useEffect(() => {
    if (queueStop) { queueStopRef.current = true; setQueueStop(false); }
  }, [queueStop]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Die Freigaben kommen vom Projekt und werden nach jeder Anfrage
  // nachgezogen. Vorher leitete der Reiter seinen Zustand aus einer
  // Ersatzgroesse ab — wo Inhalt war, stand „Freigabe ausstehend", auch wenn
  // niemand je gefragt hatte (L-79).
  const [freigaben, setFreigaben] = useState(project?.content_freigaben ?? null);
  const [anfragend, setAnfragend] = useState(null);

  useEffect(() => {
    setFreigaben(project?.content_freigaben ?? null);
  }, [project?.content_freigaben]);

  const freigabeAnfragen = async (page) => {
    setAnfragend(page.id);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/projects/${project.id}/request-page-approval`,
        { method: 'POST', headers,
          body: JSON.stringify({ seite_id: String(page.id), topic: page.page_name }) },
      );
      const daten = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(daten.detail || 'Anfrage fehlgeschlagen');

      // Die Antwort traegt den neuen Stand — kein zweiter Abruf noetig.
      setFreigaben(daten.freigaben ?? freigaben);
      toast.success(daten.email_sent
        ? `Freigabe angefragt — E-Mail an ${daten.customer_email}`
        : 'Freigabe vermerkt (keine E-Mail hinterlegt)');
      onProjectRefresh?.();
    } catch (fehler) {
      // Der Knopf meldete frueher gar nichts, weil er nichts tat. Ein
      // stiller Fehlschlag waere derselbe Zustand mit mehr Aufwand.
      toast.error(fehler.message || 'Freigabe konnte nicht angefragt werden');
    } finally {
      setAnfragend(null);
    }
  };

  const generateContent = async (page) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${project.id}/content-workshop/${page.id}`, { method: 'POST', headers });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail;
        if (detail?.code === 'BRIEFING_NOT_APPROVED') {
          toast('🔒 Briefing noch nicht freigegeben. Bitte warte auf Kundenfreigabe.', { duration: 5000 });
          if (onProjectRefresh) onProjectRefresh();
          return;
        }
        throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Fehler');
      }
      const data = await res.json();
      setPageContent(prev => ({ ...prev, [page.id]: data }));
      setSelectedPage(page);
      setActiveTab('inhalte');
      toast.success(`Content fuer "${page.page_name}" generiert`);
    } catch (e) { toast.error(e.message); }
    finally { setGenerating(false); }
  };

  const getField = (pageId, field) => editedContent[pageId]?.[field] ?? pageContent[pageId]?.[field] ?? '';
  const setEdit = (pageId, field, value) => setEditedContent(prev => ({ ...prev, [pageId]: { ...(prev[pageId] || {}), [field]: value } }));

  const generateAllContent = async () => {
    if (sitemapPages.length === 0) return;
    setQueueRunning(true);
    setQueueDone(0);
    queueStopRef.current = false;
    for (let i = 0; i < sitemapPages.length; i++) {
      if (queueStopRef.current) { toast('Generierung gestoppt', { icon: '\u25A0' }); break; }
      const page = sitemapPages[i];
      setQueueCurrentPage(page.page_name);
      try { await generateContent(page); } catch { /* weiter */ }
      setQueueDone(i + 1);
      if (i < sitemapPages.length - 1 && !queueStopRef.current) {
        await new Promise(r => setTimeout(r, 1000));
      }
    }
    setQueueRunning(false);
    setQueueCurrentPage('');
    if (!queueStopRef.current) toast.success('Alle Seiten generiert!');
  };

  const handleGenerateAll = async () => {
    if (!sitemapPages || sitemapPages.length === 0) {
      toast.error('Keine Sitemap-Seiten vorhanden. Zuerst Sitemap anlegen.');
      return;
    }
    if (!window.confirm(
      `Alle ${sitemapPages.length} Seiten mit KI-Texten befüllen?\n` +
      'Bestehende KI-Texte werden überschrieben.'
    )) return;

    setGeneratingAll(true);
    setAllGenProgress({ done: 0, total: sitemapPages.length });
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/projects/${project.id}/content-workshop/generate-all`,
        { method: 'POST', headers }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Generieren');

      setAllGenProgress({ done: data.pages_generated, total: sitemapPages.length });
      toast.success(`${data.pages_generated} Seiten erfolgreich generiert!`);

      if (data.results && Array.isArray(data.results)) {
        const newContent = { ...pageContent };
        data.results.forEach(item => {
          newContent[item.page_id] = {
            h1:               item.h1 || '',
            hero_text:        item.hero_text || '',
            abschnitt_text:   item.abschnitt_text || '',
            cta:              item.cta || '',
            meta_title:       item.meta_title || '',
            meta_description: item.meta_description || '',
          };
        });
        setPageContent(newContent);
      }
    } catch (err) {
      toast.error(err.message || 'Generierung fehlgeschlagen');
    } finally {
      setGeneratingAll(false);
    }
  };

  const saveCurrentContent = async (pageId) => {
    if (!pageId) return;
    const edited = editedContent[pageId];
    const base = pageContent[pageId];
    if (!edited && !newContent[pageId]) return;
    setSaveStatus('saving');
    try {
      await fetch(`${API_BASE_URL}/api/projects/${project.id}/page-content/${pageId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ content: { ...(base || {}), ...(edited || {}) }, new_content: newContent[pageId] || '' }),
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handlePageSelect = async (page) => {
    if (selectedPage && selectedPage.id !== page.id) {
      await saveCurrentContent(selectedPage.id);
    }
    setSelectedPage(page);
  };

  // Auto-save every 30s
  useEffect(() => {
    if (!selectedPage) return;
    const interval = setInterval(() => saveCurrentContent(selectedPage.id), 30000);
    return () => clearInterval(interval);
  }, [selectedPage?.id, editedContent, newContent]); // eslint-disable-line

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Unter-Tab-Navigation — nur anzeigen wenn kein mode gesetzt (freie Navigation) */}
      {!mode && (
        <div style={{ display: 'flex', background: 'var(--bg-app)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 12 }}>
          {CONTENT_TABS.map((tab, i) => {
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                flex: 1, padding: '10px 8px 8px', border: 'none',
                borderRight: i < CONTENT_TABS.length - 1 ? '1px solid var(--border-light)' : 'none',
                borderBottom: isActive ? '3px solid var(--brand-primary)' : '3px solid transparent',
                background: isActive ? 'var(--bg-active, var(--bg-elevated))' : 'transparent',
                cursor: 'pointer', fontFamily: 'var(--font-sans)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              }}>
                <span style={{ fontSize: 18 }}>{tab.icon}</span>
                <span style={{ fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? 'var(--brand-primary)' : 'var(--text-primary)' }}>{tab.label}</span>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{tab.desc}</span>
              </button>
            );
          })}
        </div>
      )}


      {/* TAB 2: SEITENINHALTE — Master-Detail */}
      {activeTab === 'inhalte' && (
        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', height: 640 }}>
          <div style={{ borderRight: '1px solid var(--border-light)', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
            <div style={{ padding: '8px 10px 8px 14px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{sitemapPages.length} Seiten</span>
              <button
                onClick={handleGenerateAll}
                disabled={generatingAll || !sitemapPages?.length}
                style={{
                  padding: '4px 8px', borderRadius: 6, border: 'none',
                  background: generatingAll ? '#6B7280' : '#7c3aed',
                  color: '#fff', fontSize: 12, fontWeight: 700,
                  cursor: generatingAll ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap', flexShrink: 0,
                }}
                title="Alle Seiten mit einem KI-API-Call generieren"
              >
                {generatingAll ? (
                  <>
                    <span style={{ width: 9, height: 9, border: '1.5px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .8s linear infinite', display: 'inline-block' }} />
                    {allGenProgress.done}/{allGenProgress.total}
                  </>
                ) : '🤖 Alle'}
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {sitemapPages.map(page => {
                const hasContent = !!pageContent[page.id];
                const isSelected = selectedPage?.id === page.id;
                return (
                  <div role="button" tabIndex={0} onKeyDown={aufTaste(() => handlePageSelect(page))} key={page.id} onClick={() => handlePageSelect(page)}
                    style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-light)', cursor: 'pointer', background: isSelected ? 'var(--bg-active, var(--bg-elevated))' : 'transparent', borderLeft: `3px solid ${isSelected ? 'var(--brand-primary)' : 'transparent'}` }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: isSelected ? 'var(--brand-primary)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.page_name}</span>
                      {hasContent && <span style={{ fontSize: 12, color: 'var(--status-success-text)', flexShrink: 0, marginLeft: 4 }}>{'\u2713'}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{page.page_type}</div>
                    {page.page_type === 'ground' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 6px', background: '#E0F4F8', borderRadius: 4, marginTop: 4, fontSize: 12, fontWeight: 700, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        🤖 GEO / KI-Optimierung
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {selectedPage ? (
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 1 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{selectedPage.page_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{selectedPage.page_type}{selectedPage.ziel_keyword ? ` · ${selectedPage.ziel_keyword}` : ''}</div>
                </div>
                <button onClick={() => generateContent(selectedPage)} disabled={generating}
                  style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: generating ? 'var(--border-medium)' : 'var(--brand-primary)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {generating ? (<><span style={{ width: 11, height: 11, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .8s linear infinite', display: 'inline-block' }} />Generiert...</>) : pageContent[selectedPage.id] ? 'Neu generieren' : 'KI generieren'}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, minWidth: 90 }}>
                  {saveStatus === 'saving' && (<><span style={{ width: 10, height: 10, border: '1.5px solid var(--border-medium)', borderTopColor: 'var(--brand-primary)', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block', flexShrink: 0 }} /><span style={{ color: 'var(--text-tertiary)' }}>Speichert...</span></>)}
                  {saveStatus === 'saved' && <span style={{ color: 'var(--status-success-text)', fontWeight: 600 }}>Gespeichert</span>}
                  {saveStatus === 'error' && <span style={{ color: 'var(--status-danger-text)' }}>Speicherfehler</span>}
                </div>
              </div>

              <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
                {selectedPage?.page_type === 'ground' && (
                  <button
                    onClick={async () => {
                      setGenerating(true);
                      try {
                        const res = await fetch(
                          `${API_BASE_URL}/api/projects/${project.id}/ground-page`,
                          { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
                        );
                        if (!res.ok) throw new Error(await res.text());
                        const data = await res.json();
                        setPageContent(prev => ({
                          ...prev,
                          [selectedPage.id]: {
                            headline: data.ground_page.page_title,
                            subheadline: data.ground_page.meta_description,
                            intro: data.ground_page.intro,
                            content: JSON.stringify(data.ground_page, null, 2),
                            meta_description: data.ground_page.meta_description,
                            page_name: selectedPage.page_name,
                            page_id: selectedPage.id,
                          },
                        }));
                        // **Erzeugen und Ablegen sind zweierlei** (L-179,
                        // 07.09.2026). Die Ablage schlug bis heute immer fehl
                        // — die Zieltabelle war nirgends angelegt —, und der
                        // Server meldete dafür „Generierung fehlgeschlagen".
                        // Wer den falschen Schritt genannt bekommt, sucht am
                        // falschen Ort.
                        if (data.gespeichert === false) {
                          toast.error(
                            `Ground Page erzeugt, aber nicht gespeichert: ${data.ablage_fehler || 'unbekannter Grund'}`,
                          );
                        } else {
                          toast.success('Ground Page generiert ✓');
                        }
                      } catch (e) {
                        // Den echten Grund zeigen statt eines festen Satzes.
                        toast.error(`Ground Page fehlgeschlagen: ${String(e.message || e).slice(0, 200)}`);
                      }
                      finally { setGenerating(false); }
                    }}
                    disabled={generating}
                    style={{ background: 'var(--kc-yellow)', color: '#000', border: 'none', borderRadius: 6, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '.06em', width: '100%' }}
                  >
                    🤖 Ground Page generieren (GEO)
                  </button>
                )}
                {pageContent[selectedPage.id]?.old_content && (
                  <div>
                    <FieldLabel>Alter Content (von bestehender Website)</FieldLabel>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-app)', borderRadius: 8, padding: '10px 12px', lineHeight: 1.7, maxHeight: 100, overflowY: 'auto', whiteSpace: 'pre-wrap', borderLeft: '3px solid var(--border-medium)' }}>
                      {pageContent[selectedPage.id].old_content}
                    </div>
                  </div>
                )}

                {pageContent[selectedPage.id] && (
                  <div>
                    <FieldLabel>KI-Content</FieldLabel>
                    {[
                      { field: 'headline', label: 'H1', rows: 1 },
                      { field: 'subheadline', label: 'Unterzeile', rows: 1 },
                      { field: 'intro', label: 'Einleitung', rows: 3 },
                    ].map(({ field, label, rows }) => (
                      <div key={field} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>{label}</div>
                        <textarea aria-label={label} value={getField(selectedPage.id, field)} onChange={e => setEdit(selectedPage.id, field, e.target.value)} rows={rows}
                          style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid var(--border-light)', borderRadius: 6, background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
                      </div>
                    ))}
                    {(pageContent[selectedPage.id].sections || []).map((section, i) => (
                      <div key={i} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '10px 12px', marginBottom: 8, borderLeft: '3px solid var(--brand-primary-mid, var(--border-medium))' }}>
                        <input aria-label="Abschnitt-Titel" value={editedContent[selectedPage.id]?.sections?.[i]?.titel ?? section.titel}
                          onChange={e => { const secs = [...(editedContent[selectedPage.id]?.sections || pageContent[selectedPage.id].sections.map(s => ({...s})))]; secs[i] = { ...secs[i], titel: e.target.value }; setEdit(selectedPage.id, 'sections', secs); }}
                          style={{ width: '100%', fontSize: 13, fontWeight: 700, border: 'none', background: 'transparent', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', marginBottom: 6, outline: 'none', boxSizing: 'border-box' }} placeholder="Abschnitt-Titel" />
                        <textarea aria-label="Abschnitt-Text" value={editedContent[selectedPage.id]?.sections?.[i]?.text ?? section.text}
                          onChange={e => { const secs = [...(editedContent[selectedPage.id]?.sections || pageContent[selectedPage.id].sections.map(s => ({...s})))]; secs[i] = { ...secs[i], text: e.target.value }; setEdit(selectedPage.id, 'sections', secs); }}
                          rows={3} style={{ width: '100%', fontSize: 12, border: 'none', background: 'transparent', color: 'var(--text-secondary)', fontFamily: 'var(--font-sans)', resize: 'vertical', outline: 'none', lineHeight: 1.7, boxSizing: 'border-box' }} />
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <FieldLabel>Eigene Ergaenzungen</FieldLabel>
                  <textarea aria-label="Eigene Texte, Ergaenzungen oder Hinweise fuer diese Seite" value={newContent[selectedPage.id] || ''} onChange={e => setNewContent(prev => ({ ...prev, [selectedPage.id]: e.target.value }))}
                    rows={4} placeholder="Eigene Texte, Ergaenzungen oder Hinweise fuer diese Seite"
                    style={{ width: '100%', padding: '10px 12px', fontSize: 12, border: '1px dashed var(--border-medium)', borderRadius: 8, background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7 }} />
                </div>

                {pageContent[selectedPage.id] && (
                  <div>
                    <FieldLabel>SEO</FieldLabel>
                    {[{ field: 'meta_title', label: 'Meta-Titel (max. 60 Z.)' }, { field: 'meta_description', label: 'Meta-Description (max. 155 Z.)' }, { field: 'cta', label: 'Call-to-Action' }].map(({ field, label }) => (
                      <div key={field} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>{label}</div>
                        <input aria-label={label} value={getField(selectedPage.id, field)} onChange={e => setEdit(selectedPage.id, field, e.target.value)}
                          style={{ width: '100%', padding: '7px 10px', fontSize: 12, border: '1px solid var(--border-light)', borderRadius: 6, background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }} />
                      </div>
                    ))}
                  </div>
                )}

                {!pageContent[selectedPage.id] && !generating && (
                  <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-tertiary)' }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>🤖</div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Noch kein KI-Content generiert</div>
                    <div style={{ fontSize: 12 }}>KI generieren klicken — Claude nimmt den alten Content und schreibt ihn neu.</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Seite aus der Liste waehlen</div>
          )}
        </div>
      )}

      {/* TAB 3: BILDER & ASSETS */}
      {activeTab === 'assets' && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-light)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Bilder & Assets — Zuordnung zu Seiten</div>
          {sitemapPages.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Erst Sitemap anlegen</div>
          ) : sitemapPages.map(page => (
            <details key={page.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
              <summary style={{ padding: '12px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, listStyle: 'none', userSelect: 'none' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{page.page_name}</span>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{page.page_type}</span>
              </summary>
              <div style={{ padding: '0 18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(() => {
                  const pageData = (websiteContent || []).find(p => p.url?.includes(page.page_name?.toLowerCase().replace(/ /g, '-')) || p.h1?.toLowerCase().includes(page.page_name?.toLowerCase()));
                  const imgs = Array.isArray(pageData?.images) ? pageData.images : [];
                  return imgs.length > 0 ? (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Gecrawlte Bilder ({imgs.length})</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {imgs.slice(0, 8).map((src, j) => {
                          const imgSrc = typeof src === 'string' ? src : src?.src || '';
                          return (
                            <div key={j} style={{ width: 64, height: 64, borderRadius: 8, border: '1px solid var(--border-light)', overflow: 'hidden', background: 'var(--bg-app)' }}>
                              <img src={imgSrc} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.parentNode.style.display = 'none'; }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Keine Bilder aus Crawler fuer diese Seite gefunden</div>
                  );
                })()}
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Eigene Bilder hochladen: im Analyse-Tab unter Zugangsdaten / Dateien</div>
              </div>
            </details>
          ))}
        </div>
      )}

      {/* TAB 4: FREIGABEN */}
      {activeTab === 'freigaben' && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-light)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Content-Freigaben je Seite</div>
          {sitemapPages.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Erst Sitemap anlegen</div>
          ) : sitemapPages.map(page => {
            const stand = standJeSeite(freigaben, page.id, !!pageContent[page.id]);
            const laeuft = anfragend === page.id;
            // Die Farbe folgt dem Zustand, nicht dem Vorhandensein von Text.
            const farbe = {
              freigegeben: ['var(--status-success-bg)', 'var(--status-success-text)'],
              abgelehnt:   ['var(--status-error-bg)',   'var(--status-error-text)'],
              angefragt:   ['var(--status-warning-bg)', 'var(--status-warning-text)'],
            }[stand.zustand] || ['var(--bg-elevated)', 'var(--text-tertiary)'];
            return (
              <div key={page.id} style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{page.page_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{page.page_type}</div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: farbe[0], color: farbe[1] }}>
                  {stand.text}
                </span>
                {stand.anfragbar && (
                  <button
                    onClick={() => freigabeAnfragen(page)}
                    onKeyDown={aufTaste(() => freigabeAnfragen(page))}
                    disabled={laeuft}
                    aria-label={`Freigabe f\u00fcr ${page.page_name} beim Kunden anfragen`}
                    style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--brand-primary-mid)', fontSize: 12, fontWeight: 600, cursor: laeuft ? 'default' : 'pointer', opacity: laeuft ? 0.6 : 1, fontFamily: 'var(--font-sans)' }}>
                    {laeuft ? 'Sendet\u2026' : stand.zustand === 'abgelehnt' ? 'Erneut anfragen' : 'Freigabe anfordern'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FieldLabel({ children }) {
  return <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>{children}</div>;
}
